import React, { useEffect, useMemo, useState } from "react";
import ReportHeader from "./ReportHeader";
import {
  TrainedIndicator,
  useReportQuery,
  useReportPesTestsLazyQuery,
  usePesTestResultsLazyQuery,
} from "generated/hooks";
import styles from "./PDFExport.module.scss";
import { useParams } from "react-router-dom";
import PDFFooter from "./PDFFooter";
import Page1Table, { page1Headers } from "./Page1Table";
import Page2Table, {
  page2NitrogenHeaders,
  page2SoilTextureHeaders,
} from "./Page2Table";
import Page3Table, { page3Headers } from "./Page3Table";
import Page4, { page4Headers } from "@pages/pdf-export/Page4";
import Page5 from "@pages/pdf-export/Page5";
import {
  computeAllowedIndicatorNames,
  filterIndicatorHeaders,
} from "@pages/pdf-export/indicatorLimit";
import { PartialDeep } from "type-fest";
import { ThemeProvider } from "@components/theme-provider";

export const IndicatorGradientColors = [
  "#A2EE6E",
  "#7FDF81",
  "#5FBF92",
  "#479E9D",
  "#387DA3",
  "#2A5B8C",
  "#1E3C72",
  "#0A2463",
];

type PesTestNode = NonNullable<
  NonNullable<
    NonNullable<
      NonNullable<
        ReturnType<typeof useReportPesTestsLazyQuery>[1]["data"]
      >["reports"]
    >["nodes"]
  >[number]["pesTests"]
>["nodes"];

export const PDFExport: React.FC = () => {
  const { id, key } = useParams();
  const reportId = id?.trim();
  const authContext = {
    headers: { Authorization: `Bearer PES_${key}` },
  };

  const { data, loading, error } = useReportQuery({
    variables: { id: reportId! },
    context: authContext,
    skip: !reportId,
  });

  const [fetchTests] = useReportPesTestsLazyQuery();
  const [fetchResults] = usePesTestResultsLazyQuery();
  const [allTests, setAllTests] = useState<NonNullable<PesTestNode>>([]);
  const [testsLoading, setTestsLoading] = useState(true);

  const report = data?.reports?.nodes?.[0];

  useEffect(() => {
    if (!reportId || !report) {
      return;
    }

    let cancelled = false;

    const loadAllTests = async () => {
      // 1. Accumulate all tests (paginating through pesTests pages)
      const accumulated: NonNullable<PesTestNode> = [];
      let cursor: string | undefined;
      let hasNextPage = true;

      while (hasNextPage) {
        const { data: pageData } = await fetchTests({
          variables: { id: reportId, after: cursor },
          context: authContext,
        });

        const page = pageData?.reports?.nodes?.[0]?.pesTests;
        const nodes = page?.nodes ?? [];
        accumulated.push(...nodes);

        if (cancelled) {
          return;
        }

        hasNextPage = Boolean(page?.pageInfo.hasNextPage);
        if (hasNextPage) {
          cursor = page?.pageInfo.endCursor ?? undefined;
        }
      }

      // 2. For each test whose results have more pages, fetch all remaining
      for (let i = 0; i < accumulated.length; i++) {
        const test = accumulated[i];
        const resultsPageInfo = test.results?.pageInfo;
        if (!resultsPageInfo?.hasNextPage || !resultsPageInfo.endCursor) {
          continue;
        }

        const existingNodes = [...(test.results?.nodes ?? [])];
        let resultsCursor: string | undefined = resultsPageInfo.endCursor;

        while (resultsCursor) {
          const { data: resultsData } = await fetchResults({
            variables: { testId: test.id, after: resultsCursor },
            context: authContext,
          });
          if (cancelled) {
            return;
          }

          const resultsPage = resultsData?.pesTest?.results;
          existingNodes.push(...(resultsPage?.nodes ?? []));

          if (!resultsPage?.pageInfo?.hasNextPage || !resultsPage.pageInfo.endCursor) {
            break;
          }
          resultsCursor = resultsPage.pageInfo.endCursor;
        }

        // Replace the test entry with all results merged
        accumulated[i] = {
          ...test,
          results: {
            ...test.results,
            nodes: existingNodes,
            pageInfo: { hasNextPage: false, endCursor: null },
          },
        } as (typeof accumulated)[number];
      }

      if (!cancelled) {
        setAllTests(accumulated);
        setTestsLoading(false);
      }
    };

    loadAllTests();
    return () => {
      cancelled = true;
    };
  }, [report?.id, reportId]);

  const indicatorNamesAndIds = useMemo(
    () =>
      allTests
        .slice()
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
        )
        .reduce(
          (acc, test) => {
            const results = test.results?.nodes ?? [];
            results.forEach((result) => {
              if (
                result.indicator &&
                !acc.some((item) => item.id === result.indicator!.id) &&
                result.indicator.model?.name === test.originalModelName
              ) {
                acc.push({
                  id: result.indicator.id,
                  name: result.indicator.indicatorDefinition?.name ?? "",
                  displayName:
                    result.indicator.indicatorDefinition?.displayName ??
                    result.indicator.indicatorDefinition?.name ??
                    "",
                  description:
                    result.indicator.indicatorDefinition?.description ?? "",
                  unit: result.indicator.indicatorDefinition?.unit ?? "",
                  trainedIndicator: result.indicator,
                });
              }
            });
            return acc;
          },
          [] as {
            id: string;
            name: string;
            displayName: string;
            description: string;
            unit: string;
            trainedIndicator: PartialDeep<TrainedIndicator>;
          }[]
        ),
    [allTests]
  );

  // Per-customer indicator limit: hide columns (and whole pages) the customer
  // may not see.
  const visibleHeaders = useMemo(() => {
    const allowed = computeAllowedIndicatorNames(allTests);
    return {
      biological: filterIndicatorHeaders(page1Headers, allowed),
      nitrogen: filterIndicatorHeaders(page2NitrogenHeaders, allowed),
      soilTexture: filterIndicatorHeaders(page2SoilTextureHeaders, allowed),
      chemical: filterIndicatorHeaders(page3Headers, allowed),
      carbonNitrogen: filterIndicatorHeaders(page4Headers, allowed),
    };
  }, [allTests]);

  if (loading || testsLoading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return (
      <div>
        There was an error generating your report. Please try again later.{" "}
        {JSON.stringify(error)}
      </div>
    );
  }

  if (!reportId) {
    return <div>Missing report ID.</div>;
  }

  if (!report || !indicatorNamesAndIds) {
    return <div>Loading</div>;
  }

  if (allTests.length > 0) {
    setTimeout(() => {
      (window as any).gotenbergready = "go!";
    }, 2500);
  }

  // Page 2 groups nitrogen and soil texture in one table; label it by what
  // survived the limit.
  const page2Title =
    visibleHeaders.soilTexture.length === 0
      ? "Nitrogen"
      : visibleHeaders.nitrogen.length === 0
        ? "Soil Texture"
        : "Nitrogen and Soil Texture";

  const indicatorPages = [
    {
      key: "biological",
      visible: visibleHeaders.biological.length > 0,
      heading: <h2 className={styles.subtitle}>Biological Indicators</h2>,
      content: (
        <Page1Table
          allTests={allTests}
          indicatorNamesAndIds={indicatorNamesAndIds}
          headers={visibleHeaders.biological}
        />
      ),
    },
    {
      key: "texture",
      visible:
        visibleHeaders.nitrogen.length > 0 ||
        visibleHeaders.soilTexture.length > 0,
      heading: (
        <div className={styles.subtitleContainer}>
          <h2 className={styles.subtitleP2}>{page2Title}</h2>
        </div>
      ),
      content: (
        <Page2Table
          allTests={allTests}
          indicatorNamesAndIds={indicatorNamesAndIds}
          nitrogenHeaders={visibleHeaders.nitrogen}
          soilTextureHeaders={visibleHeaders.soilTexture}
        />
      ),
    },
    {
      key: "chemical",
      visible: visibleHeaders.chemical.length > 0,
      heading: <h2 className={styles.subtitle}>Chemical Indicators</h2>,
      content: (
        <Page3Table
          allTests={allTests}
          indicatorNamesAndIds={indicatorNamesAndIds}
          headers={visibleHeaders.chemical}
        />
      ),
    },
    {
      key: "carbonNitrogen",
      visible: visibleHeaders.carbonNitrogen.length > 0,
      heading: <h2 className={styles.subtitle}>Carbon and Nitrogen</h2>,
      content: (
        <Page4
          allTests={allTests}
          indicatorNamesAndIds={indicatorNamesAndIds}
          headers={visibleHeaders.carbonNitrogen}
        />
      ),
    },
  ].filter((page) => page.visible);

  return (
    <ThemeProvider defaultTheme="light">
      <ReportHeader
        reportNumber={report.name}
        clientName={report.createdBy?.fullName ?? "Unknown"}
        email={report.createdBy?.email ?? ""}
        date={new Date().toLocaleDateString()}
      />
      <table className={`paging ${styles.pagingTable}`}>
        <thead>
          <tr>
            <td className={styles.pagingTd}>&nbsp;</td>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <main className={styles.container}>
                {/* Indicator pages: one per group, skipped entirely when the
                    customer's indicator limit leaves the group empty. */}
                {indicatorPages.map((page, index) => (
                  <div
                    key={page.key}
                    className={styles.page}
                    style={
                      index > 0 ? { pageBreakBefore: "always" } : undefined
                    }
                    data-testid={`pdf-page-${page.key}`}
                  >
                    {index === 0 ? (
                      <h1 className={styles.title}>Soil Report</h1>
                    ) : null}
                    {page.heading}
                    {page.content}
                  </div>
                ))}

                {/* Glossary page */}
                <div
                  className={styles.page}
                  style={
                    indicatorPages.length > 0
                      ? { pageBreakBefore: "always" }
                      : undefined
                  }
                  data-testid="pdf-page-glossary"
                >
                  {indicatorPages.length === 0 ? (
                    <>
                      <h1 className={styles.title}>Soil Report</h1>
                      <p data-testid="pdf-no-indicators">
                        No indicator results are available for this report
                        under your account&apos;s indicator settings. Please
                        contact PES Technologies if you believe this is an
                        error.
                      </p>
                    </>
                  ) : null}
                  <Page5
                    indicators={indicatorNamesAndIds.map(
                      (indicator) => indicator.trainedIndicator!
                    )}
                  />
                </div>
              </main>
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td className={styles.pagingTd}>&nbsp;</td>
          </tr>
        </tfoot>
      </table>
      <PDFFooter pageNumber={0} />
    </ThemeProvider>
  );
};
